import { useState, useEffect, useMemo, useRef } from 'react'
import useSWRMutation from 'swr/mutation'
import { z } from 'zod'
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { Button } from '@/components/Button'
import { FormField } from '@/components/FormField'
import { SuccessMessage } from '@/components/SuccessMessage'
import { FormError } from '@/components/FormError'
import { Delete } from '@/components/Icons'
import { fetcher } from '@/lib/fetcher'

const callbackRequestSchema = z.object({
  specialistName: z.string().optional(),
  specialistEmail: z.string().optional(),
  name: z.string().min(1, 'Name is required.'),
  phone: z.string().min(1, 'Phone number is required.'),
  email: z.string().nonempty('Email is required.').email('Please enter a valid email address.'),
  preferredContactTime: z.string().min(1, 'Preferred contact time is required.'),
  region: z.string().optional(),
  additionalInformation: z.string().optional()
})

const CONTACT_TIME_OPTIONS = [
  '9am - 12pm',
  '12pm - 5pm',
  'Anytime'
]

const useForm = (initialData, validationSchema) => {
  const initialDataRef = useRef(initialData)
  
  // Update ref when initialData changes
  useEffect(() => {
    initialDataRef.current = initialData
  }, [initialData])
  
  const [formData, setFormData] = useState(initialData)
  const [errors, setErrors] = useState({})

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateForm = () => {
    if (!validationSchema) return true

    const result = validationSchema.safeParse(formData)
    if (!result.success) {
      const formErrors = z.flattenError(result.error)
      const newErrors = {}
      for (const field in formErrors.fieldErrors) {
        const messages = formErrors.fieldErrors[field]
        if (messages && messages.length > 0) {
          newErrors[field] = messages[0]
        }
      }
      setErrors(newErrors)
      return false
    }
    setErrors({})
    return true
  }

  const resetForm = () => {
    setFormData(initialDataRef.current)
    setErrors({})
  }

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    handleInputChange,
    validateForm,
    resetForm
  }
}

const CallbackForm = ({ formData, errors, handleInputChange, onSubmit, isSubmitting, regionOptions }) => (
  <form className='space-y-6'>
    {formData.specialistName && (
      <FormField
        label='Specialist'
        id='specialistName'
        name='specialistName'
        value={formData.specialistName}
        readOnly
        className='pb-2'
      />
    )}

    <FormField
      label='Name'
      id='name'
      name='name'
      value={formData.name}
      onChange={handleInputChange}
      placeholder='Enter your full name'
      error={errors.name}
      required
      className='pb-2'
    />

    <div className='grid grid-cols-1 sm:grid sm:grid-cols-2 gap-6'>
      <FormField
        label='Phone number'
        id='phone'
        name='phone'
        type='tel'
        value={formData.phone}
        onChange={handleInputChange}
        placeholder='Enter your phone number'
        error={errors.phone}
        required
        className='pb-2'
      />

      <FormField
        label='Email address'
        id='email'
        name='email'
        type='email'
        value={formData.email}
        onChange={handleInputChange}
        placeholder='you@example.com'
        error={errors.email}
        required
        className='pb-2'
      />
    </div>

    <div className='grid grid-cols-1 sm:grid sm:grid-cols-2 gap-6'>
      <FormField
        type='select'
        label='Preferred contact time'
        id='preferredContactTime'
        name='preferredContactTime'
        value={formData.preferredContactTime}
        onChange={handleInputChange}
        options={CONTACT_TIME_OPTIONS}
        placeholder='Select'
        error={errors.preferredContactTime}
        required
        className='pb-2'
      />

      <FormField
        label='Region'
        id='region'
        name='region'
        type='select'
        value={formData.region}
        onChange={handleInputChange}
        options={regionOptions}
        placeholder='Select'
        className='pb-2'
        optional
      />
    </div>

    <FormField
      type='textarea'
      label='Additional information'
      id='additionalInformation'
      name='additionalInformation'
      value={formData.additionalInformation}
      onChange={handleInputChange}
      placeholder='Any additional information you would like to share...'
      className='pb-2'
      optional
    />

    <FormError error={errors.submit} />

    <div className='lg:text-right'>
      <Button
        onClick={onSubmit}
        disabled={isSubmitting}
        className='w-full lg:w-auto'
      >
        {isSubmitting ? 'Submitting...' : 'Submit'}
      </Button>
    </div>
  </form>
)

export const CallbackRequestDrawer = ({ open, onClose, specialist, regionOptions }) => {
  const initialFormData = useMemo(() => ({
    specialistName: specialist?.Name || '',
    specialistEmail: specialist?.Email || '',
    name: '',
    phone: '',
    email: '',
    preferredContactTime: '',
    region: '',
    additionalInformation: ''
  }), [specialist])

  const {
    formData,
    setFormData,
    errors,
    setErrors,
    handleInputChange,
    validateForm,
    resetForm
  } = useForm(initialFormData, callbackRequestSchema)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)

  const { trigger: submitCallbackRequest, isMutating } = useSWRMutation(
    '/api/specialist/callback',
    fetcher
  )

  useEffect(() => {
    if (open) {
      // Reset form to initial state when drawer opens, ensuring specialistEmail is included
      // Use initialFormData directly to avoid dependency on resetForm function
      setFormData(initialFormData)
      setErrors({})
    }
  }, [open, initialFormData, setFormData, setErrors])

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      const validatedData = callbackRequestSchema.parse(formData)
      await submitCallbackRequest({
        method: 'POST',
        body: JSON.stringify(validatedData),
        headers: { 'Content-Type': 'application/json' }
      })
      setShowConfirmation(true)
    } catch (error) {
      console.error('Error submitting callback request:', error)
      setErrors({ submit: 'Failed to submit request. Please try again.' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting && !isMutating) {
      onClose()
      // Form will be reset when drawer opens next time via useEffect
      setTimeout(() => setShowConfirmation(false), 1000)
    }
  }

  const handleConfirmationClose = () => {
    setShowConfirmation(false)
    onClose()
    // Form will be reset when drawer opens next time via useEffect
  }

  return (
    <Dialog open={open} onClose={handleClose} className='relative z-[100]'>
      <DialogBackdrop
        transition
        className='fixed inset-0 bg-gray-500/75 transition-opacity duration-500 ease-in-out data-[closed]:opacity-0'
      />

      <div className='fixed inset-0 overflow-hidden'>
        <div className='absolute inset-0 overflow-hidden'>
          <div className='pointer-events-none fixed inset-y-0 right-0 flex max-w-full'>
            <DialogPanel
              transition
              className='pointer-events-auto w-screen max-w-[615px] transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700'
            >
              <div className='flex h-full flex-col overflow-y-auto bg-white  shadow-xl'>
                <div className='flex items-center justify-between py-7 px-8'>
                  <DialogTitle className='text-2xl font-extrabold text-gray-950 pb-px'>
                    {specialist?.Name
                      ? `Let ${specialist.Name} call you back`
                      : 'Let us call you back'}
                  </DialogTitle>
                  <div className=' flex h-7 items-center'>
                    <button
                      type='button'
                      onClick={handleClose}
                      disabled={isSubmitting || isMutating}
                      className='outline-none inline-flex items-center justify-center size-[44px] rounded-full hover:bg-primary-100 active:bg-primary-100 disabled:pointer-events-none'
                    >
                      <span className='sr-only'>Close panel</span>
                      <Delete className='size-6' />
                    </button>
                  </div>
                </div>
                <hr className='border-default-200' />
                <div className={`relative ${showConfirmation ? 'flex flex-col justify-center grow' : ''} p-8`}>
                  {showConfirmation && (
                    <SuccessMessage
                      message={specialist?.Name
                        ? `Your request has been submitted. ${specialist.Name} will contact you shortly.`
                        : 'Your request has been submitted. A UDC representative will contact you shortly.'}
                      onClose={handleConfirmationClose}
                    />
                  )}
                  {!showConfirmation && (
                    <CallbackForm
                      formData={formData}
                      errors={errors}
                      handleInputChange={handleInputChange}
                      onSubmit={handleSubmit}
                      isSubmitting={isSubmitting || isMutating}
                      regionOptions={regionOptions}
                    />
                  )}
                </div>
                {showConfirmation && (
                  <>
                    <hr className='border-default-200' />
                    <div className='p-8 lg:text-right'>
                      <Button
                        variant='secondary'
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className='w-full lg:w-auto'
                      >
                        Close
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  )
}


